import * as moment from 'moment';
import { AUJS_FORMS } from '@app/forms/shared/aujs-forms';
import { AuthorizationService } from '@app/security/shared/authorization.service';
import { ConfigProfile } from '@app/truck-tracking/config-profile.model';
import { DivisionService } from '@app/divisions/shared/divisions.service';
import { GroupOption } from '@app/reports/report-settings/group-option.model';
import { Grouping } from '@app/reports/report-settings/grouping.model';
import { IDivision } from '@app/divisions/shared/division.interface';
import { IGrouping } from '@app/reports/report-settings/grouping.interface';
import { IMaterial } from '@app/materials/shared/material.interface';
import { IPlantAppSetting } from '@app/reports/report-settings/report-settings.interface';
import { IReportSetting } from '@app/reports/report-settings/report-setting.interface';
import { IReportSettingsManager } from '@app/reports/report-settings/report-settings-manager.interface';
import { Injectable } from '@angular/core';
import { LoadSchedulerUiConfig } from '@app/truck-tracking/ui-config/load-scheduler-ui-config';
import { Logger } from '@app/logger/logger';
import { MaterialService } from '@app/materials/shared/materials.service';
import { Moment } from 'moment';
import { Observable, of, throwError } from 'rxjs';
import { PartialPlant } from '@app/plants/shared/partial-plant.model';
import { PlantService } from '@app/plants/shared/plant.service';
import { QuickFormOption } from '@app/reports/report-settings/shared/quick-form-option.model';
import { ReportSettingsManager } from '@app/reports/report-settings/report-settings-manager.model';
import { ReportTypeService } from '@app/reports/report-types/report-types.service';
import { Setting } from '@app/reports/report-settings/setting.model';
import { SystemType } from '@app/shared/services/system-type/system-type.service';
import { first } from 'rxjs/operators';
import { hasValue } from '@app/shared/comparison-helpers.service';
import {
  truckGraphTypes,
  xAxisOptions
} from '@app/reports/truck-demand/truck-demand-graph-options.service';

const invalidSettingIds = ['addSetting', 'count', 'merge', 'removeSetting', 'settingIds'];

@Injectable({ providedIn: 'root' })
export class ReportSettingsService {
  constructor(
    private authorizationService: AuthorizationService,
    private divisionService: DivisionService,
    private log: Logger,
    private materialService: MaterialService,
    private plantService: PlantService,
    private reportTypeService: ReportTypeService
  ) {}

  /*
   * Private Method for Report AppSetting Object;
   */
  private createSetting(settingParams: any): IReportSetting {
    if (
      invalidSettingIds.includes(settingParams.id) === true ||
      settingParams.id.toString().length === 0
    ) {
      this.log.error('Invalid setting ID: ' + settingParams.id);
    }

    return new Setting(settingParams);
  }

  public getBooleanSetting(): IReportSetting {
    const settingParams = {
      title: 'Title',
      id: 'boolean',
      type: 'boolean',
      value: false,
      options: undefined
    };

    const setting = this.createSetting(settingParams);

    setting.getAppSettingValue = () => {
      return setting.value === true;
    };

    setting.loadAppSettingValue = (val: boolean) => {
      if (typeof val !== 'undefined' && val !== null) {
        setting.value = val === true;
      }
    };

    return setting;
  }

  public getDateSetting(): IReportSetting {
    const settingParams = {
      title: 'Date',
      id: 'date',
      type: 'date',
      value: moment.utc(),
      options: undefined,
      configs: {
        autoResetDaily: true,
        lastChangeDate: moment.utc(),
        lastValue: moment.utc(),
        visible: false
      },
      displayTextFunction(): string {
        return this.value?.local().format('L');
      }
    };
    const setting = this.createSetting(settingParams);

    setting.getAppSettingValue = () => {
      if (!this.dayValuesAreEqual(setting.configs.lastValue, moment(setting.value))) {
        setting.configs.lastChangeDate = moment.utc();
        setting.configs.lastValue = setting.value;
      }
      return {
        configs: {
          autoResetDaily: setting.configs.autoResetDaily,
          lastChangeDate: setting.configs.lastChangeDate,
          lastValue: setting.configs.lastValue,
          visible: setting.configs.visible
        },
        value: setting.value
      };
    };

    setting.loadAppSettingValue = (val: any) => {
      if (typeof val !== 'undefined' && val !== null) {
        setting.configs.autoResetDaily =
          val && val.configs && val.configs.autoResetDaily !== undefined
            ? val.configs.autoResetDaily
            : setting.configs.autoResetDaily;
        setting.configs.lastChangeDate = moment.utc(
          val && val.configs && val.configs.lastChangeDate !== undefined
            ? val.configs.lastChangeDate
            : setting.configs.lastChangeDate
        );
        setting.configs.lastValue = moment.utc(
          val && val.configs && val.configs.lastValue !== undefined
            ? val.configs.lastValue
            : setting.configs.lastValue
        );
        setting.configs.visible = hasValue(val.configs?.visible)
          ? val.configs.visible
          : setting.configs.visible;

        setting.value = moment.utc(val.value);
        this.onLoad(setting);
      }
    };

    return setting;
  }

  private onLoad(setting: IReportSetting): void {
    if (
      setting.configs.autoResetDaily === true &&
      !this.dayValuesAreEqual(moment.utc(), setting.configs.lastChangeDate)
    ) {
      setting.value = moment.utc();
      setting.configs.lastChangeDate = moment.utc();
      setting.configs.lastValue = setting.value;
    }
  }

  private dayValuesAreEqual(firstDay: Moment, secondDay: Moment): boolean {
    const dateCompFormat = 'YYYYMMDD';
    return firstDay.local().format(dateCompFormat) === secondDay.local().format(dateCompFormat);
  }

  public getProfileSetting(): IReportSetting {
    const settingParams = {
      title: 'Profile',
      id: 'profiles',
      type: 'profileSelect',
      value: undefined,
      showSystemType: true,
      options: [],
      filter: [],
      optionGroupings: []
    };

    const setting = this.createSetting(settingParams);

    setting.getAppSettingValue = () => {
      return setting.value;
    };

    setting.loadAppSettingValue = (profile: ConfigProfile) => {
      setting.value = Object.assign(new ConfigProfile(), profile);
    };

    return setting;
  }

  public getUILayoutSetting(): IReportSetting {
    const settingParams = {
      title: 'Grid Configuration',
      id: 'gridConfiguration',
      type: 'multiselect',
      value: undefined,
      showSystemType: true,
      options: [],
      filter: [],
      optionGroupings: []
    };

    const setting = this.createSetting(settingParams);

    setting.getAppSettingValue = () => {
      if (setting.options.length) {
        return setting.options;
      } else {
        return LoadSchedulerUiConfig;
      }
    };

    setting.loadAppSettingValue = (lssUiConfigs: any[]) => {
      setting.options = [].concat.apply([], lssUiConfigs);
      setting.options.forEach((opt) => {
        opt.selected = !opt.hide;
      });
    };

    return setting;
  }

  public getPlantsSetting(): IReportSetting {
    let readyObserver: Observable<Array<PartialPlant>>;
    const groupSystemTypes = new Grouping('systemType', 'System Types');
    const groupDivisions = new Grouping('divisionId', 'Divisions');

    const settingParams = {
      title: 'Plants',
      id: 'plants',
      type: 'multiselect',
      value: undefined,
      showSystemType: true,
      options: [],
      filter: [],
      whenReady: () => {
        return readyObserver;
      },
      configs: {
        visible: false
      },
      optionGroupings: [groupSystemTypes, groupDivisions],
      displayTextFunction(): string {
        return setting.options
          .filter((plant: any) => {
            if (plant.selected && plant.visible) {
              return true;
            }
          })
          .map((plant) => plant.name)
          .join(', ');
      }
    };

    const setting = this.createSetting(settingParams);

    let plantDivisions = [];
    this.divisionService.getDivisions().subscribe((divisions: Array<IDivision>) => {
      const divisionObjGrouping = {};
      divisions.forEach((division) => {
        if (divisionObjGrouping[division.id]) {
          divisionObjGrouping[division.id].push(division);
        } else {
          divisionObjGrouping[division.id] = [division];
        }
      });

      plantDivisions = Object.keys(divisionObjGrouping).map((key) => {
        return divisionObjGrouping[key][0];
      });
    });

    // Filter object has the properties `id` and `data`
    //
    // Supported filters:
    //   systemType: array(int)
    setting.applyFilters = () => {
      setting.filter.map((filter: any) => {
        switch (filter.type) {
          case 'systemType':
            setting.options
              .filter((plant: any) => {
                // Return if plant system type is NOT in the filter
                return filter.data.indexOf(plant.systemType) === -1;
              })
              .map((plant: any) => {
                // Set the plants visibility to hidden
                plant.visible = false;
                plant.selected = false;
              });
            break;
        }
      });
    };

    readyObserver = this.plantService.getPlants();

    readyObserver.subscribe((plants: Array<PartialPlant>) => {
      setting.options = plants.map((plant: PartialPlant) => {
        return Object.assign(new PartialPlant(), plant);
      });

      setting.options.map((plant) => {
        plant.visible = true;
        plant.selected = false;
        // To add abbrevation in same plant object.
        plantDivisions
          .filter((option) => option.id === plant.divisionId)
          .map((o) => {
            plant.divisionAbbrevation = o.abbreviation;
          });
        if (plant.systemType === 1) {
          plant.selected = true;
        }
      });
      setting.applyFilters();

      this.updateDivisionGrouping(setting, groupDivisions, plantDivisions);
      this.updateSystemGrouping(setting, groupSystemTypes);

      return true;
    });

    setting.getAppSettingValue = (): IPlantAppSetting => {
      return {
        configs: {
          visible: setting.configs.visible
        },
        value: setting.options
          .filter((plant: any) => plant.selected && plant.visible)
          .map((plant) => plant.id)
      };
    };

    setting.loadAppSettingValue = (val: IPlantAppSetting | Array<number>) => {
      // DEPRECATION 20200605/v2.21.0 This block added for backwards
      // compatibility and should be removed after 2021/06/05
      if (Array.isArray(val)) {
        val = {
          configs: { visible: false },
          value: val
        };
      }
      // END DEPRECATION

      const objVal = val as IPlantAppSetting;

      if (objVal.value?.length) {
        setting.options.map((plant: any) => {
          plant.selected = plant.visible && objVal.value.indexOf(plant.id) > -1;
        });
      } else {
        setting.options.map((plant: any) => {
          plant.selected = false;
        });
      }

      setting.configs.visible = hasValue(objVal.configs?.visible)
        ? objVal.configs.visible
        : setting.configs.visible;
    };

    return setting;
  }

  private updateDivisionGrouping(
    setting: IReportSetting,
    groupDivisions: IGrouping,
    plantDivisions: any
  ): void {
    const divisions = Array.from(
      new Set(
        setting.options
          .filter((option) => option.visible === true)
          .map((p: any) => {
            return p.divisionId;
          })
      )
    );

    groupDivisions.clearOptions();
    if (divisions.length > 1 && plantDivisions.length > 0) {
      divisions.map((divisionId) => {
        const matchedDivision = plantDivisions.find(
          (division: IDivision) => division.id === divisionId
        );
        if (matchedDivision) {
          groupDivisions.addOption(
            new GroupOption(matchedDivision.id, matchedDivision.abbreviation)
          );
        }
      });
    }
  }

  private updateSystemGrouping(setting: IReportSetting, groupSystemTypes: IGrouping): void {
    const systemTypesArray = Object.values(SystemType);
    const systemGroupings = systemTypesArray.filter((item: any) => {
      return (
        item.systemTypeID ===
        Array.from(
          new Set(
            setting.options
              .filter((plant: any) => {
                return plant.visible;
              })
              .map((plant) => {
                return plant.systemType;
              })
          )
        ).find((obj) => obj === item.systemTypeID)
      );
    });

    groupSystemTypes.clearOptions();
    systemGroupings.map((sg) => {
      groupSystemTypes.addOption(new GroupOption(sg.id, sg.name));
    });
  }

  public getQuickFormsSetting(): IReportSetting {
    let permissionsLoaded = false;
    let readyObserver: Observable<boolean>;

    const settingParams = {
      title: 'Quick Forms',
      id: 'forms',
      type: 'multiselect',
      value: undefined,
      options: [],
      filter: [],
      whenReady: () => {
        return readyObserver;
      }
    };

    const setting = this.createSetting(settingParams);

    readyObserver = this.authorizationService.formPermissionsLoadedObserver.pipe(
      first((isPermissionLoaded) => isPermissionLoaded === true)
    );

    readyObserver.subscribe(() => {
      this.setupBasedOnPermissions(setting);
      permissionsLoaded = true;
    });

    setting.getAppSettingValue = () => {
      return setting.options
        .filter((option: any) => {
          return option.selected && option.visible;
        })
        .map((option: any) => {
          return option.id;
        });
    };

    setting.loadAppSettingValue = (selectedFormIds: Array<string>) => {
      if (permissionsLoaded) {
        if (Array.isArray(selectedFormIds) === false) {
          return;
        }

        setting.options.forEach((option: any) => {
          option.selected = option.visible && selectedFormIds.indexOf(option.id) > -1;
        });
      } else {
        this.authorizationService.formPermissionsLoadedObserver
          .pipe(first((isPermissionLoaded) => isPermissionLoaded === true))
          .subscribe(() => {
            permissionsLoaded = true;
            setting.loadAppSettingValue(selectedFormIds);
          });
      }
    };

    return setting;
  }

  private setupBasedOnPermissions(setting: IReportSetting): void {
    setting.options.splice(0, setting.options.length);

    // Note that two pairs turns the constant into an array of pairs such that
    // the first element is the text of the object property and the second element
    // is the value.
    const forms: any = Object.keys(AUJS_FORMS).map((data) => [data, AUJS_FORMS[data]]);
    const availableForms = forms.filter((form: any) => {
      return form[1].enabled && form[1].availableInQuickMenu;
    });

    availableForms.forEach((form) => {
      const formPermission = this.authorizationService.getFormPermission(form[1].id);
      if (formPermission.userCanView() || form[1].alwaysShow) {
        const newOption = new QuickFormOption();
        newOption.id = form[0];
        newOption.name = form[1].longName;
        newOption.visible = true;
        newOption.selected = false;
        newOption.legacy = form[1].legacy;
        setting.options.push(newOption);
      }
    });

    setting.options = setting.options.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }
      if (a.name > b.name) {
        return 1;
      }

      return 0;
    });
  }

  public getMaterialsSetting(): IReportSetting {
    let readyObserver: Observable<Array<IMaterial>>;

    const settingParams = {
      title: 'Raw Material',
      id: 'material',
      type: 'autoComplete',
      value: undefined,
      showSystemType: true,
      options: [],
      optionIdProperty: 'id',
      filter: [],
      required: true,
      configs: {
        visible: false,
        displayText: undefined
      },
      whenReady: () => {
        return readyObserver;
      },
      displayTextFunction(): string {
        return this.value?.name;
      }
    };
    const setting = this.createSetting(settingParams);

    setting.refresh = () => {
      readyObserver = this.materialService.getMaterials(setting.filter);
      readyObserver.subscribe(
        (materials: Array<IMaterial>) => {
          // Make sure not to change the address so that if it is being
          // watched the changes can getList picked up.
          setting.options.splice(0, setting.options.length);
          Array.prototype.push.apply(setting.options, materials);
          // Set default value if a value is not set or valid.
          if (
            typeof setting.value === 'undefined' ||
            (setting.value === null && setting.options.length > 0)
          ) {
            setting.value = setting.options[0];
          }
        },
        (err) => {
          return throwError('Error loading Materials from API.' + err);
        }
      );
      return readyObserver;
    };

    setting.getAppSettingValue = () => {
      if (typeof setting.value !== 'undefined' && setting.value !== null) {
        return {
          configs: {
            visible: setting.configs.visible,
            displayText: setting.configs.displayText
          },
          value: setting.value.id
        };
      } else {
        return setting.options;
      }
    };

    setting.loadAppSettingValue = (materialData: any) => {
      if (typeof readyObserver === 'undefined' || readyObserver === null) {
        setting.refresh();
      }

      readyObserver.subscribe(() => {
        if (typeof materialData !== 'undefined' && materialData !== null) {
          setting.options.map((material: IMaterial) => {
            if (materialData.value === material.id) {
              setting.value = material;
              setting.configs.displayText = material.name;
            }
          });
          setting.configs.visible =
            materialData && materialData.configs && materialData.configs.visible !== undefined
              ? materialData.configs.visible
              : setting.configs.visible;
        }
      });
    };
    return setting;
  }

  public getNameSetting(): IReportSetting {
    const settingParams = {
      title: 'Title',
      id: 'smallText',
      type: 'smallText',
      value: '',
      options: undefined
    };
    const setting = this.createSetting(settingParams);

    setting.getAppSettingValue = () => {
      return setting.value;
    };

    setting.loadAppSettingValue = (val: any) => {
      if (val) {
        setting.value = val;
      }
    };

    return setting;
  }

  public getReportTypeSetting(): IReportSetting {
    const settingParams = {
      title: 'Report Type',
      id: 'reportTypeSelect',
      type: 'singleSelect',
      value: undefined,
      options: [],

      whenReady: () => {
        return of('');
      }
    };
    const setting = this.createSetting(settingParams);

    this.authorizationService.formPermissionsLoadedObserver
      .pipe(first((isPermissionLoaded) => isPermissionLoaded === true))
      .subscribe(() => {
        this.loadWhenPermissionsReady(setting);
      });

    setting.getAppSettingValue = () => {
      return setting.value.id;
    };

    setting.loadAppSettingValue = (val: string) => {
      this.authorizationService.formPermissionsLoadedObserver
        .pipe(first((isPermissionLoaded) => isPermissionLoaded === true))
        .subscribe(() => {
          if (typeof val !== 'undefined' && val !== null) {
            setting.value = setting.options.find((option) => option.id === val);
          }
        });
    };
    return setting;
  }

  public getGraphTypeSetting(): IReportSetting {
    const settingParams = {
      title: 'Graph Type',
      id: 'graphType',
      type: 'singleSelect',
      value: undefined,
      options: truckGraphTypes
    };
    const setting = this.createSetting(settingParams);

    setting.getAppSettingValue = () => {
      return setting.value?.id;
    };

    setting.loadAppSettingValue = (val: any) => {
      if (hasValue(val)) {
        setting.value = setting.options.find((option) => option.id === val);
      }
    };

    return setting;
  }

  public getXAxisLabelSetting(): IReportSetting {
    const settingParams = {
      title: 'X-Axis Label',
      id: 'xAxisLabel',
      type: 'singleSelect',
      value: undefined,
      options: xAxisOptions
    };
    const setting = this.createSetting(settingParams);

    setting.getAppSettingValue = () => {
      return setting.value?.id;
    };

    setting.loadAppSettingValue = (val: any) => {
      if (hasValue(val)) {
        setting.value = setting.options.find((option) => option.id === val);
      }
    };

    return setting;
  }

  private loadWhenPermissionsReady(setting: IReportSetting): void {
    setting.options = this.reportTypeService.getReportTypesForUser();
  }

  public getNewReportSettings(): IReportSettingsManager {
    return new ReportSettingsManager();
  }
}
